import * as React from "react";
import Dialog, { DialogProps } from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import { IconButton, Paper, Typography, Zoom } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { APP_COLORS } from "../../enums";
import { DefaultButton, PrimaryButton } from "../form";
import { TransitionProps } from "@mui/material/transitions";
import { ModalBg } from "../../assets/images";

export default function ModalBox({
  title,
  primaryButtonText = "Save Changes",
  secondaryButtonText = "Discard Changes",
  children,
  maxWidth,
  onClose,
  fullScreen = false,
  showFooter = true,
  onPrimaryButtonClick,
  open = true,
  primaryButtonProps = {},
  dialogContentProps = {},
  disableGradient,
}: any) {
  const [scroll, setScroll] = React.useState<DialogProps["scroll"]>("paper");

  const handleCloseModal = (event, reason) => {
    if (reason && reason == "backdropClick") return;
    onClose();
  };

  const descriptionElementRef = React.useRef<HTMLElement>(null);
  React.useEffect(() => {
    if (open) {
      const { current: descriptionElement } = descriptionElementRef;
      if (descriptionElement !== null) {
        descriptionElement.focus();
      }
    }
  }, [open]);

  return (
    <Dialog
      open={open}
      onClose={handleCloseModal}
      scroll={scroll}
      aria-labelledby="scroll-dialog-title"
      aria-describedby="scroll-dialog-description"
      fullScreen={fullScreen}
      fullWidth
      maxWidth={maxWidth}
      keepMounted
      disableEscapeKeyDown
      TransitionComponent={Transition}
      PaperProps={{
        sx: {
          background: disableGradient
            ? "white"
            : `linear-gradient(180deg, ${APP_COLORS.headerIconColor} 0%, ${APP_COLORS.white} 100%)`,
          ...(fullScreen && {
            height: "100%",
            maxHeight: "100%",
          }),
        },
      }}
    >
      <Paper sx={{
        background: disableGradient ? "white" : `url(${ModalBg}) center/cover no-repeat`,
        p: 3,
        ...(fullScreen && {
          height: "100%",
          display: "flex",
          flexDirection: "column",
        }),
      }} elevation={0}>
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={(theme) => ({
            position: "absolute",
            right: 8,
            top: 8,
            color: APP_COLORS.text,
          })}
        >
          <CloseIcon />
        </IconButton>
        <Typography
          variant="heading4-helveticaNeue-medium"
          color={APP_COLORS.text}
          textAlign="center"
          my={2}
        >
          {title}
        </Typography>
        {children}
        {showFooter && (
          <DialogActions>
            {secondaryButtonText && (
              <DefaultButton fullWidth onClick={onClose}>
                {secondaryButtonText}
              </DefaultButton>
            )}
            {primaryButtonText && (
              <PrimaryButton
                onClick={onPrimaryButtonClick}
                fullWidth
                {...primaryButtonProps}
              >
                {primaryButtonText}
              </PrimaryButton>
            )}
          </DialogActions>
        )}
      </Paper>
    </Dialog>
  );
}

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement<any, any>;
  },
  ref: React.Ref<unknown>
) {
  return <Zoom in={true} ref={ref} {...props} />;
});
